import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Layout from './components/layout/Layout';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import Dashboard from './pages/Dashboard';
import PublishWizard from './pages/PublishWizard';
import PostsPage from './pages/Posts';
import AnalyticsPage from './pages/Analytics';
import ProjectsPage from './pages/Projects';
import AccountsPage from './pages/Accounts';
import ProvidersPage from './pages/Providers';
import AutomationPage from './pages/Automation';
import UsersPage from './pages/Users';
import SettingsPage from './pages/Settings';
import TermsPage from './pages/Terms';
import PrivacyPage from './pages/Privacy';
import MediaLibraryPage from './pages/MediaLibrary';
import CalendarPage from './pages/Calendar';
import BulkPublishPage from './pages/BulkPublish';
import AdsManagerPage from './pages/AdsManager';
import CRMPage from './pages/CRMPage';
import ReservationsPage from './pages/Reservations';
import ProductsPage from './pages/Products';
import PricingPage from './pages/Pricing';
import MyBillingPage from './pages/MyBilling';
import BillingAdminPage from './pages/BillingAdmin';
import PlatformSettingsPage from './pages/PlatformSettingsPage';
import './styles/globals.css';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30000 } } });

// ── 404 ──────────────────────────────────────────────────
function NotFoundPage() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', background: 'var(--bg-base)' }}>
      <span style={{ fontSize: '72px' }}>🌊</span>
      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: '800' }}>404</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>Esta página no existe</p>
      <a href="/" style={{ color: 'var(--accent-primary)', fontWeight: '600', textDecoration: 'none', fontSize: '14px' }}>← Volver al dashboard</a>
    </div>
  );
}

// ── Onboarding ───────────────────────────────────────────
function OnboardingGuard({ children }) {
  const { user } = useAuth();
  const [done, setDone] = React.useState(() => localStorage.getItem(`onboarding_done_${user?.id}`) === '1');
  const [step, setStep] = React.useState(0);

  if (done || !user) return children;

  const steps = [
    { icon: '⚙️', title: 'Configura una integración', desc: 'Ve a Configuración → Integraciones y añade las credenciales OAuth de tus redes sociales.', action: '/settings', label: 'Ir a Configuración' },
    { icon: '🔗', title: 'Conecta una cuenta', desc: 'En la sección Cuentas conecta tu primera red social usando el botón Conectar.', action: '/accounts', label: 'Ir a Cuentas' },
    { icon: '🤖', title: 'Agrega un proveedor de IA', desc: 'En Proveedores de IA añade tu API Key para que la IA genere captions automáticamente.', action: '/providers', label: 'Ir a Proveedores' },
    { icon: '⚡', title: '¡Publica tu primer contenido!', desc: 'Ya estás listo. Sube una imagen o video y publica en todas tus redes con un clic.', action: '/publish', label: 'Publicar ahora' },
  ];

  const finish = () => { localStorage.setItem(`onboarding_done_${user?.id}`, '1'); setDone(true); };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div className="animate-scaleIn" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '32px', maxWidth: '480px', width: '100%', boxShadow: 'var(--shadow-lg)' }}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '28px' }}>
            {steps.map((_, i) => <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', background: i <= step ? 'var(--accent-primary)' : 'var(--border)', transition: 'background 0.3s' }} />)}
          </div>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '52px', marginBottom: '12px' }}>{steps[step].icon}</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: '800', marginBottom: '8px' }}>{steps[step].title}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.6 }}>{steps[step].desc}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={finish} style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', fontFamily: 'var(--font-body)' }}>Saltar tour</button>
            {step < steps.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} style={{ flex: 2, padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--gradient-primary)', color: '#fff', border: 'none', fontSize: '13px', fontWeight: '700', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>{steps[step].label} →</button>
            ) : (
              <button onClick={() => { finish(); window.location.href = steps[step].action; }} style={{ flex: 2, padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--gradient-primary)', color: '#fff', border: 'none', fontSize: '13px', fontWeight: '700', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>🚀 {steps[step].label}</button>
            )}
          </div>
          <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)', marginTop: '16px' }}>Paso {step + 1} de {steps.length}</p>
        </div>
      </div>
      {children}
    </>
  );
}

// ── Role Guard ───────────────────────────────────────────
function RoleGuard({ children, role = 'admin' }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== role) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '12px' }}>
      <span style={{ fontSize: '48px' }}>🔒</span>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No tienes permisos para acceder a esta sección.</p>
    </div>
  );
  return children;
}

// ── Owner Guard ──────────────────────────────────────────
function OwnerGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !user.is_platform_owner) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '12px' }}>
      <span style={{ fontSize: '48px' }}>🔒</span>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No tienes permisos para acceder a esta sección.</p>
    </div>
  );
  return children;
}

// ── Auth Guard ───────────────────────────────────────────
function AuthGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', background: 'var(--bg-base)' }}>
      <img src="/thitan-logo.png" alt="TX-Flow AI" style={{ width: '72px', height: '72px', objectFit: 'contain', animation: 'float 2s ease infinite' }} />
      <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Cargando TX-Flow AI...</div>
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
}

// ── PWA Install Banner ───────────────────────────────────
function PwaInstallBanner() {
  const [prompt, setPrompt] = React.useState(null);
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPrompt(e); if (!localStorage.getItem('pwa-dismissed')) setShow(true); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);
  const install = async () => { if (!prompt) return; prompt.prompt(); const { outcome } = await prompt.userChoice; if (outcome === 'accepted') setShow(false); };
  const dismiss = () => { setShow(false); localStorage.setItem('pwa-dismissed', '1'); };
  if (!show) return null;
  return (
    <div className="pwa-banner">
      <img src="/thitan-logo.png" alt="TX-Flow AI" style={{ width: '40px', height: '40px', objectFit: 'contain', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>Instalar TX-Flow AI</div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Acceso rápido desde tu pantalla de inicio</div>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
        <button onClick={dismiss} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px', padding: '6px', borderRadius: '6px' }}>✕</button>
        <button onClick={install} style={{ background: 'var(--gradient-primary)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700', padding: '8px 14px', borderRadius: 'var(--radius-md)' }}>Instalar</button>
      </div>
    </div>
  );
}

// ── Routes ───────────────────────────────────────────────
function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/terms"   element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/login"   element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />


      <Route path="/" element={<AuthGuard><OnboardingGuard><Layout /></OnboardingGuard></AuthGuard>}>
        <Route index                element={<Dashboard />} />
        <Route path="publish"       element={<PublishWizard />} />
        <Route path="bulk"          element={<BulkPublishPage />} />
        <Route path="ads"           element={<AdsManagerPage />} />
        <Route path="crm" element={<CRMPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="products"       element={<ProductsPage />} />
        <Route path="billing"       element={<MyBillingPage />} />
        <Route path="pricing"       element={<PricingPage />} />
        <Route path="billing-admin" element={<RoleGuard role="admin"><BillingAdminPage /></RoleGuard>} />
        <Route path="platform-settings" element={<OwnerGuard><PlatformSettingsPage /></OwnerGuard>} />
        <Route path="posts"         element={<PostsPage />} />
        <Route path="analytics"     element={<AnalyticsPage />} />
        <Route path="calendar"      element={<CalendarPage />} />
        <Route path="projects"      element={<ProjectsPage />} />
        <Route path="accounts"      element={<AccountsPage />} />
        <Route path="media"         element={<MediaLibraryPage />} />
        <Route path="providers"     element={<RoleGuard role="admin"><ProvidersPage /></RoleGuard>} />
        <Route path="automation"    element={<AutomationPage />} />
        <Route path="settings"      element={<RoleGuard role="admin"><SettingsPage /></RoleGuard>} />
        <Route path="users"         element={<RoleGuard role="admin"><UsersPage /></RoleGuard>} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// ── App ──────────────────────────────────────────────────
export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <PwaInstallBanner />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: { background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '14px', fontFamily: 'var(--font-body)', boxShadow: 'var(--shadow-lg)', marginBottom: 'calc(var(--bottom-nav-height) + 8px)' },
              success: { duration: 3500, iconTheme: { primary: '#10b981', secondary: '#fff' } },
              error:   { duration: 8000, iconTheme: { primary: '#ef4444', secondary: '#fff' } },
              duration: 3500,
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}
